import classNames from 'classnames'
import { motion } from 'framer-motion'
import { useContext } from 'react'

import { TextColorType } from '@ui/design-system/colors/colors'

import {
  SegmentControlContext,
  SegmentedControlSize,
  SegmentedControlTab,
  SegmentedControlVariant,
} from './SegmentedControl'

import styles from './SegmentedControl.module.scss'

interface SegmentItemProps {
  tab: SegmentedControlTab
  isActive: boolean
  size: SegmentedControlSize
  variant?: SegmentedControlVariant
  disabled?: boolean
  iconSize?: number
  iconColor?: TextColorType
  secondaryIconColor?: TextColorType

  onSelect: () => void
}

export const SegmentItem: React.FC<SegmentItemProps> = ({
  tab,
  isActive,
  size,
  variant,
  disabled,
  iconSize,
  iconColor,
  secondaryIconColor,
  onSelect,
}) => {
  const context = useContext(SegmentControlContext)
  if (!context) {
    throw new Error('SegmentItem must be used within a SegmentedControl component')
  }
  const { layoutId, animate } = context

  const { iconLeft: IconLeft, iconRight: IconRight } = tab
  const inactiveIconColor = variant === 'primary' ? 'text-primary-low-em' : 'text-low-em'
  const iconAppearanceColor = isActive ? iconColor : inactiveIconColor
  const iconAppearanceSecondaryColor = isActive ? secondaryIconColor : inactiveIconColor

  return (
    <button
      type='button'
      disabled={disabled}
      className={classNames(styles.option, styles[size], styles[variant || 'default'], {
        [styles.active]: isActive,
        [styles.disabled]: disabled,
      })}
      onClick={onSelect}
    >
      {isActive && (
        <motion.div
          layoutId={animate ? layoutId : undefined}
          className={styles.highlight}
          transition={{ type: 'spring', stiffness: 200, damping: 25 }}
        />
      )}
      <span className={styles.label}>
        {IconLeft && (
          <IconLeft
            size={iconSize}
            color={iconAppearanceColor}
            secondaryColor={iconAppearanceSecondaryColor}
          />
        )}
        {tab.name}
        {IconRight && (
          <IconRight
            size={iconSize}
            color={iconAppearanceColor}
            secondaryColor={iconAppearanceSecondaryColor}
          />
        )}
      </span>
    </button>
  )
}
